import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  IconButton,
  Button,
  TextField,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import CheckIcon from '@material-ui/icons/Check';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { formattedCurrency } from '../../../utils/utils';
import './saleMobile.css';

const SaleMobileQuantityModal = ({
  open,
  onClose,
  item = null,
  currentAmount = 1,
  onConfirm,
  onRemove,
}) => {
  const [inputValue, setInputValue] = useState('1');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && item) {
      const initialAmt = Number(currentAmount) > 0 ? Number(currentAmount) : 1;
      setInputValue(String(initialAmt));
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 150);
    }
  }, [open, item, currentAmount]);

  if (!item) return null;

  const unitPrice = Number(item.numericValue || item.valueUn || item.value || 0);

  // Parse numérico seguro aceitando vírgula e ponto
  const parseQty = (valStr) => {
    const clean = String(valStr || '').trim().replace(',', '.');
    const n = parseFloat(clean);
    return isNaN(n) || n < 0 ? 0 : n;
  };

  const parsedAmount = parseQty(inputValue);
  const lineSubtotal = Math.round((parsedAmount * unitPrice) * 100) / 100;

  const handleInputChange = (e) => {
    const val = e.target.value;
    // Aceita dígitos, ponto ou vírgula
    if (/^[0-9]*[.,]?[0-9]*$/.test(val) || val === '') {
      setInputValue(val);
    }
  };

  const handleSetExact = (val) => {
    const rounded = Math.round(val * 1000) / 1000;
    setInputValue(String(rounded));
    if (inputRef.current) inputRef.current.focus();
  };

  const handleAddDelta = (delta) => {
    const next = Math.max(0.001, Math.round((parsedAmount + delta) * 1000) / 1000);
    setInputValue(String(next));
    if (inputRef.current) inputRef.current.focus();
  };

  const handleSave = () => {
    const finalQty = Math.max(0.001, Math.round(parsedAmount * 10000) / 10000);
    if (finalQty <= 0) {
      if (onRemove) onRemove();
    } else {
      if (onConfirm) onConfirm(finalQty);
    }
    onClose();
  };

  const handleRemoveItem = () => {
    if (onRemove) onRemove();
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          maxHeight: '85vh',
          backgroundColor: '#ffffff',
        },
      }}
    >
      <div className="sale-mobile-drawer-content" style={{ maxHeight: '85vh', height: 'auto' }}>
        {/* Header */}
        <div className="sale-mobile-drawer-header" style={{ padding: '12px 16px' }}>
          <div className="sale-mobile-drawer-title">
            <EditOutlinedIcon style={{ color: '#17497f', fontSize: 22 }} />
            <span>Definir Quantidade</span>
          </div>
          <IconButton size="small" onClick={onClose} style={{ color: '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Informações do Item */}
        <div style={{ padding: '14px 16px 8px 16px' }}>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
            {item.name}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#17497f' }}>
              Valor Unitário: {formattedCurrency(unitPrice)}
            </span>
            {item.barCode && (
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                Cód: {item.barCode}
              </span>
            )}
          </div>
        </div>

        {/* Campo de Entrada Principal com Destaque */}
        <div style={{ padding: '8px 16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f8fafc',
              border: '2px solid #17497f',
              borderRadius: 14,
              padding: '6px 12px',
              gap: 10,
            }}
          >
            <input
              ref={inputRef}
              type="text"
              inputMode="decimal"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
              }}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                backgroundColor: 'transparent',
                fontSize: '1.8rem',
                fontWeight: 900,
                textAlign: 'center',
                color: '#0f172a',
              }}
              placeholder="0,000"
            />
          </div>

          {/* Subtotal Calculado Instantâneo */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 10,
              padding: '8px 12px',
              backgroundColor: '#f0fdf4',
              borderRadius: 10,
              border: '1px solid #bbf7d0',
            }}
          >
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#15803d' }}>
              Total deste item:
            </span>
            <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#166534' }}>
              {formattedCurrency(lineSubtotal)}
            </span>
          </div>

          {/* Atalhos Rápidos de Quantidade e Fração */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '0.74rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 6 }}>
              Quantidades rápidas:
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
              <button
                type="button"
                className="sale-mobile-qty-chip"
                onClick={() => handleSetExact(0.25)}
              >
                0.25
              </button>
              <button
                type="button"
                className="sale-mobile-qty-chip"
                onClick={() => handleSetExact(0.5)}
              >
                0.50
              </button>
              <button
                type="button"
                className="sale-mobile-qty-chip"
                onClick={() => handleSetExact(1.0)}
              >
                1.00
              </button>
              <button
                type="button"
                className="sale-mobile-qty-chip"
                onClick={() => handleSetExact(2.0)}
              >
                2.00
              </button>
            </div>

            {/* Ajustes incrementais */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 6 }}>
              <button
                type="button"
                className="sale-mobile-qty-chip-step"
                onClick={() => handleAddDelta(-0.5)}
              >
                - 0.5
              </button>
              <button
                type="button"
                className="sale-mobile-qty-chip-step"
                onClick={() => handleAddDelta(-0.1)}
              >
                - 0.1
              </button>
              <button
                type="button"
                className="sale-mobile-qty-chip-step"
                onClick={() => handleAddDelta(0.1)}
              >
                + 0.1
              </button>
              <button
                type="button"
                className="sale-mobile-qty-chip-step"
                onClick={() => handleAddDelta(0.5)}
              >
                + 0.5
              </button>
            </div>
          </div>
        </div>

        {/* Rodapé com Ações */}
        <div style={{ padding: '12px 16px 18px 16px', display: 'flex', gap: 10, marginTop: 6 }}>
          {onRemove && (
            <button
              type="button"
              onClick={handleRemoveItem}
              style={{
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                border: '1px solid #fca5a5',
                borderRadius: 12,
                padding: '12px 16px',
                fontSize: '0.88rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <DeleteOutlineIcon style={{ fontSize: 18 }} />
              Remover
            </button>
          )}

          <button
            type="button"
            onClick={handleSave}
            style={{
              flex: 1,
              backgroundColor: '#17497f',
              color: '#ffffff',
              border: 'none',
              borderRadius: 12,
              padding: '12px 16px',
              fontSize: '0.95rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(23, 73, 127, 0.25)',
            }}
          >
            <CheckIcon style={{ fontSize: 18 }} />
            Salvar Quantidade
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default SaleMobileQuantityModal;
